"use client";

import { useState } from "react";
import { Building2, Users, Crown, Shield, AlertCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Label } from "@/app/_module/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { Textarea } from "@/app/_module/components/ui/textarea";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";

interface AgencyCreationStepProps {
  onAgencyCreate: (agencyData: AgencyData) => void;
  onBack: () => void;
  isLoading?: boolean;
  selectedPlan: 'lite' | 'basic' | 'full_fledged' | 'super_agent';
  // Invite context for sub-agency creation
  inviteContext?: {
    agencyId: string;
    agencyName: string;
    role?: string;
  };
}

interface AgencyData {
  name: string;
  description?: string;
  contactEmail?: string;
  role: 'agent' | 'owner'; // Maps to agency_users.role
  /** Self-reported downline headcount. Shown to admin + synced to GHL. */
  downlineAgentCount?: number;
}

export default function AgencyCreationStep({
  onAgencyCreate,
  onBack,
  isLoading = false,
  selectedPlan,
  inviteContext
}: AgencyCreationStepProps) {
  const [agencyName, setAgencyName] = useState("");
  const [agencyDescription, setAgencyDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [downlineAgentCount, setDownlineAgentCount] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<'agent' | 'owner'>('owner');
  const [errors, setErrors] = useState<{
    agencyName?: string;
    contactEmail?: string;
    downlineAgentCount?: string;
  }>({});

  // Only show agency creation for full_fledged plans
  if (selectedPlan !== 'full_fledged') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-yellow-500" />
          <h2 className="text-3xl font-bold text-gray-900">Agency Creation Not Available</h2>
          <p className="mt-2 text-gray-600">
            Agency creation is only available for Full‑Fledged plans.
          </p>
        </div>
        <div className="flex justify-center">
          <Button onClick={onBack} variant="outline">
            Back to Plan Selection
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { agencyName?: string; contactEmail?: string; downlineAgentCount?: string } = {};

    if (!agencyName.trim()) {
      newErrors.agencyName = "Agency name is required";
    } else if (agencyName.trim().length < 2) {
      newErrors.agencyName = "Agency name must be at least 2 characters";
    }

    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    // Downline count: required on full-fledged agency signup, integer 0..100000.
    let parsedDownline: number | undefined;
    const raw = downlineAgentCount.trim();
    if (raw === "") {
      newErrors.downlineAgentCount = "Please enter a number (use 0 if you're solo).";
    } else {
      const n = Number(raw);
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        newErrors.downlineAgentCount = "Must be a whole number.";
      } else if (n < 0) {
        newErrors.downlineAgentCount = "Must be 0 or greater.";
      } else if (n > 100000) {
        newErrors.downlineAgentCount = "That seems too high. Please double-check.";
      } else {
        parsedDownline = n;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onAgencyCreate({
        name: agencyName.trim(),
        description: agencyDescription.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        role: selectedRole,
        downlineAgentCount: parsedDownline,
      });
    }
  };

  // Check if this is an invited agent creating a sub-agency
  const isInvitedAgent = !!(inviteContext?.agencyId && inviteContext?.agencyName);
  const parentAgencyName = inviteContext?.agencyName || 'Parent Agency';

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900">
          {isInvitedAgent
            ? `Create Your Sub-Agency (Optional)`
            : 'Create Your Agency (Optional)'}
        </h2>
        <p className="mt-2 text-gray-600">
          {isInvitedAgent
            ? `Set up your own sub-agency within ${parentAgencyName}, or skip to work directly as an agent`
            : 'Set up your agency now, or skip to work as an independent agent initially'}
        </p>
      </div>

      {/* Skip for Now Info */}
      <div className={`${isInvitedAgent ? 'bg-purple-50 border-purple-200' : 'bg-primary/5 border-primary/20'} border rounded-lg p-4`}>
        <div className="flex items-start gap-3">
          <div className={`${isInvitedAgent ? 'bg-purple-100' : 'bg-primary/10'} p-2 rounded-lg`}>
            <AlertCircle className={`h-4 w-4 ${isInvitedAgent ? 'text-purple-600' : 'text-primary'}`} />
          </div>
          <div>
            <h4 className={`font-medium ${isInvitedAgent ? 'text-purple-900' : 'text-primary'} mb-1`}>
              {isInvitedAgent
                ? `Creating a sub-agency within ${parentAgencyName}`
                : 'You can create your agency later'}
            </h4>
            <p className={`text-sm ${isInvitedAgent ? 'text-purple-700' : 'text-primary/80'}`}>
              {isInvitedAgent ? (
                <>
                  You were invited to join <span className="font-semibold">{parentAgencyName}</span>.
                  Creating a sub-agency allows you to build your own team while still being part of {parentAgencyName}.
                  You'll inherit their carriers and can add more. If you skip, you'll work directly as an agent under {parentAgencyName}.
                </>
              ) : (
                <>
                  With Full‑Fledged, you already have agency owner privileges. You can work as an independent agent now
                  and create your agency later when you're ready to build a team.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isInvitedAgent ? 'Sub-Agency Information' : 'Agency Information'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Agency Name */}
            <div className="space-y-2">
              <Label htmlFor="agencyName">
                {isInvitedAgent ? 'Sub-Agency Name *' : 'Agency Name *'}
              </Label>
              <Input
                id="agencyName"
                placeholder={isInvitedAgent ? 'Enter your sub-agency name' : 'Enter your agency name'}
                value={agencyName}
                onChange={(e) => {
                  setAgencyName(e.target.value);
                  if (errors.agencyName) {
                    setErrors(prev => ({ ...prev, agencyName: undefined }));
                  }
                }}
                className={errors.agencyName ? 'border-red-500' : ''}
              />
              {errors.agencyName && (
                <p className="text-sm text-red-600">{errors.agencyName}</p>
              )}
            </div>

            {/* Agency Description */}
            <div className="space-y-2">
              <Label htmlFor="agencyDescription">Description (Optional)</Label>
              <Textarea
                id="agencyDescription"
                placeholder="Brief description of your agency"
                value={agencyDescription}
                onChange={(e) => setAgencyDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label htmlFor="contactEmail">Contact Email (Optional)</Label>
              <Input
                id="contactEmail"
                type="email"
                placeholder="agency@example.com"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  if (errors.contactEmail) {
                    setErrors(prev => ({ ...prev, contactEmail: undefined }));
                  }
                }}
                className={errors.contactEmail ? 'border-red-500' : ''}
              />
              {errors.contactEmail && (
                <p className="text-sm text-red-600">{errors.contactEmail}</p>
              )}
            </div>

            {/* Downline headcount — helps onboarding size the setup appropriately */}
            <div className="space-y-2">
              <Label htmlFor="downlineAgentCount">
                How many agents are in your downline? *
              </Label>
              <Input
                id="downlineAgentCount"
                type="number"
                inputMode="numeric"
                min={0}
                max={100000}
                step={1}
                placeholder=""
                value={downlineAgentCount}
                onChange={(e) => {
                  setDownlineAgentCount(e.target.value);
                  if (errors.downlineAgentCount) {
                    setErrors(prev => ({ ...prev, downlineAgentCount: undefined }));
                  }
                }}
                className={errors.downlineAgentCount ? 'border-red-500' : ''}
              />
              <p className="text-xs text-gray-500">
                To help us set up your system accordingly. Enter 0 if you're starting solo.
              </p>
              {errors.downlineAgentCount && (
                <p className="text-sm text-red-600">{errors.downlineAgentCount}</p>
              )}
            </div>

            {/* Role Selection - Only Agency Owner can create agencies */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Your Role</Label>
              <div className={`flex items-start space-x-3 p-4 border rounded-lg ${isInvitedAgent ? 'bg-purple-50 border-purple-200' : 'bg-primary/5 border-primary/20'}`}>
                <div className={`${isInvitedAgent ? 'bg-purple-100' : 'bg-primary/10'} p-2 rounded-lg`}>
                  <Crown className={`h-4 w-4 ${isInvitedAgent ? 'text-purple-600' : 'text-primary'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-medium ${isInvitedAgent ? 'text-purple-900' : 'text-primary'}`}>
                      {isInvitedAgent ? 'Sub-Agency Owner' : 'Agency Owner'}
                    </span>
                    <Badge variant="secondary" className={`text-xs ${isInvitedAgent ? 'bg-purple-100 text-purple-700' : 'bg-primary/10 text-primary'}`}>
                      Required
                    </Badge>
                  </div>
                  <p className={`text-sm ${isInvitedAgent ? 'text-purple-700' : 'text-primary/80'}`}>
                    {isInvitedAgent ? (
                      <>
                        As the sub-agency creator under <span className="font-semibold">{parentAgencyName}</span>,
                        you will have full control over your team, manage your agents, and settings while
                        maintaining connection to the parent agency.
                      </>
                    ) : (
                      <>
                        As the agency creator, you will be the owner with full control over your agency,
                        manage agents, billing, and settings.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className={`${isInvitedAgent ? 'bg-purple-50 border-purple-200' : 'bg-primary/5 border-primary/20'} border rounded-lg p-4`}>
              <div className="flex items-start gap-3">
                <Shield className={`h-5 w-5 ${isInvitedAgent ? 'text-purple-600' : 'text-primary'} mt-0.5 flex-shrink-0`} />
                <div>
                  <h4 className={`font-medium ${isInvitedAgent ? 'text-purple-900' : 'text-primary'} mb-1`}>
                    {isInvitedAgent
                      ? `What you get as Sub-Agency Owner:`
                      : 'What you get as Agency Owner:'}
                  </h4>
                  <ul className={`text-sm ${isInvitedAgent ? 'text-purple-700' : 'text-primary/80'} space-y-1`}>
                    {isInvitedAgent ? (
                      <>
                        <li>• Build and manage your own team within {parentAgencyName}</li>
                        <li>• Inherit {parentAgencyName}'s carriers + add your own</li>
                        <li>• Access to advanced analytics for your sub-agency</li>
                        <li>• Invite and manage your own agents</li>
                        <li>• Maintain connection to parent agency resources</li>
                        <li>• And many more</li>
                      </>
                    ) : (
                      <>
                        <li>• Invite, manage, and assign leads to your agents</li>
                        <li>• Create sub-agencies and build out downlines</li>
                        <li>• Team-wide reporting on calls, texts, and conversions</li>
                        <li>• Control subscription, phone numbers, and A2P registration</li>
                        <li>• Set commissions, roles, and agent permissions</li>
                        <li>• And many more</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t">
              <Button type="button" variant="outline" onClick={onBack} disabled={isLoading} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </Button>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    // Skip agency creation - user will have agency_owner role but no agency
                    onAgencyCreate({
                      name: '', // Empty name indicates no agency created
                      description: undefined,
                      contactEmail: undefined,
                      role: 'owner'
                    });
                  }}
                  disabled={isLoading}
                  className="min-w-[120px] flex items-center gap-2"
                >
                  <span>Skip for Now</span>
                </Button>
                <Button
                  type="submit"
                  disabled={!agencyName.trim() || isLoading}
                  className="min-w-[120px] flex items-center gap-2"
                >
                  {isLoading ? <LoadingSpinner /> : (
                    <>
                      <span>{isInvitedAgent ? 'Create Sub-Agency' : 'Create Agency'}</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
